import { Button, EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { api, type SentimentTab } from "~/core/api/index";
import { useT } from "~/core/i18n";
import { brand } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { MetricPanel, Series } from "~/modules/analytics/components/chart";
import { Drill, DrillLink, useDetail } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { readingPath } from "~/modules/analytics/lib/loop";
import { sourceIdentity } from "~/modules/analytics/lib/sources";

export function InsightSentiment({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { push } = useDetail();

	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"insights",
			"sentiment",
			{
				range: filters.range,
				engine: filters.engine,
				locale: filters.locale,
				topics: filters.topics,
				intent: filters.intent,
			},
		],
		queryFn: () => api.insights.sentiment(entityId, filters),
		...read,
	});

	return (
		<StageShell
			entityId={entityId}
			reading="sentiment"
			lede={t("sentiment.lede")}
			filters={filters}
			setFilters={setFilters}
		>
			<Async state={state}>
				{(data) => {
					if (data.mentions === 0)
						return (
							<>
								<EmptyState
									size="compact"
									title={t("sentiment.empty")}
									description={t("sentiment.emptyBody")}
									action={<DrillLink to={readingPath(brand(entityId), "prompts")}>{t("empty.goPrompts")}</DrillLink>}
								/>
								<NegativeEvaluations entityId={entityId} data={data} />
							</>
						);
					const progress = data.mentions === 0 ? 0 : (data.read / data.mentions) * 100;

					return (
						<>
							{data.read === 0 ? (
								<div className="notice" style={{ marginBottom: "var(--s-9)" }}>
									<strong>{t("sentiment.building")}</strong>
									<p className="muted">{t("sentiment.buildingBody")}</p>
									<span className="bar">
										<span style={{ width: "2%" }} />
									</span>
								</div>
							) : (
								<p className="faint" style={{ marginBottom: "var(--s-9)" }}>
									{t("sentiment.read", { read: data.read, total: data.mentions, percent: Math.round(progress) })}
								</p>
							)}

							<MetricPanel
								metric="sentiment"
								title={t("metric.sentiment")}
								lede={t("metric.sentiment.hint")}
								block={data.block}
								rankLabel={t("insights.brand")}
								chart={<Series metric="sentiment" points={data.block.series} assets={data.block.assets} />}
							/>

							<Section title={t("perception.sentiment")} lede={t("perception.sentimentHint")}>
								<div className="row-actions">
									{data.moods.map((mood) => (
										// "unread" is not a mood the answers were filed under;
										// it is the rows the reading pass has not seen, and
										// there is no query for those.
										<Drill
											key={mood.kind}
											className={moodClass(mood.kind)}
											view={{
												kind: "answers",
												label: t(`perception.mood.${mood.kind}`),
												filters: { named: "yes", sentiment: mood.kind === "unread" ? undefined : mood.kind },
											}}
										>
											{t(`perception.mood.${mood.kind}`)} · {mood.n}
										</Drill>
									))}
								</div>
							</Section>

							<NegativeEvaluations entityId={entityId} data={data} />

							{data.themes.length > 0 ? (
								<Section title={t("perception.themes")} lede={t("perception.themesHint")}>
									<div className="row-actions">
										{data.themes.map((theme) => (
											<Drill
												key={theme.theme}
												className="pill"
												view={{ kind: "answers", label: theme.theme, filters: { theme: theme.theme } }}
											>
												{theme.theme}
												<span className="faint"> {theme.n}</span>
											</Drill>
										))}
									</div>
								</Section>
							) : null}

							{data.quotes.length > 0 ? (
								<Section title={t("perception.quotes")} lede={t("perception.quotesHint")}>
									<div className="opps">
										{data.quotes.map((quote) => (
											<Button
												variant="default"
												type="button"
												className="card card-button h-auto flex-col items-start justify-start whitespace-normal p-(--s-8) text-left"
												key={quote.sample_id}
												onClick={() => push({ kind: "answer", label: quote.question, sampleId: quote.sample_id })}
											>
												<p>{quote.descriptor}</p>
												<p className="faint">
													{quote.question}
													<br />
													{t(`engine.${quote.engine}`)} · {t("perception.rank", { n: quote.position })}
													{quote.sentiment ? ` · ${t(`perception.mood.${quote.sentiment}`)}` : ""}
												</p>
											</Button>
										))}
									</div>
								</Section>
							) : null}
						</>
					);
				}}
			</Async>
		</StageShell>
	);
}

/** Semantic colour means state, and being recommended or warned against is one. */
function moodClass(kind: string): string {
	if (kind === "positive") return "pill pill-success";
	if (kind === "negative") return "pill pill-danger";
	return "pill";
}

export function NegativeEvaluations({
	entityId,
	data,
}: {
	entityId: string;
	data: Pick<SentimentTab, "read" | "negative_reviews">;
}) {
	const t = useT();
	const base = `/w/${entityId}`;
	return (
		<Section title={t("sentiment.negative.title")} lede={t("sentiment.negative.hint")}>
			{data.negative_reviews.length === 0 ? (
				<EmptyState
					size="compact"
					title={t(data.read === 0 ? "sentiment.negative.pending" : "sentiment.negative.empty")}
				/>
			) : (
				<>
					<div className="table-scroll">
						<Table variant="resource">
							<TableHeader>
								<TableRow>
									<TableHead className="wrap">{t("sentiment.negative.answer")}</TableHead>
									<TableHead className="wrap">{t("sentiment.negative.reason")}</TableHead>
									<TableHead className="wrap">{t("sentiment.negative.action")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{data.negative_reviews.map((review) => (
									<TableRow key={review.sample_id}>
										<TableCell className="wrap">
											<Drill view={{ kind: "answer", label: review.question, sampleId: review.sample_id }}>
												{review.question}
											</Drill>
											<p>{review.descriptor || t("sentiment.negative.noSummary")}</p>
											<p className="faint">{t(`engine.${review.engine}`)}</p>
										</TableCell>
										<TableCell className="wrap">
											{review.themes.length > 0 ? (
												<p>{t("sentiment.negative.themes", { themes: review.themes.join(" · ") })}</p>
											) : null}
											<p className="muted">
												{t(review.claims.length > 0 ? "sentiment.negative.mismatch" : "sentiment.negative.noReason")}
											</p>
											{review.claims.map((claim) => (
												<div key={claim.id}>
													<p>{t("sentiment.negative.claim", { claim: claim.claim })}</p>
													<p>{t("sentiment.negative.truth", { truth: claim.truth })}</p>
													{claim.source_urls.map((source) => {
														const href = sourceIdentity(source);
														return href ? (
															<p key={source}>
																<a href={href} target="_blank" rel="noreferrer">
																	{source}
																</a>
															</p>
														) : null;
													})}
												</div>
											))}
										</TableCell>
										<TableCell className="wrap">
											<p>{t(review.claims.length > 0 ? "sentiment.negative.correct" : "sentiment.negative.context")}</p>
											<div className="row-actions">
												<DrillLink to={review.claims.length > 0 ? readingPath(base, "factcheck") : `${base}/settings`}>
													{t(review.claims.length > 0 ? "sentiment.negative.verify" : "sentiment.negative.facts")}
												</DrillLink>
												<DrillLink to={readingPath(base, "briefs")}>{t("sentiment.negative.brief")}</DrillLink>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
					<Drill
						view={{
							kind: "answers",
							label: t("sentiment.negative.all"),
							filters: { named: "yes", sentiment: "negative" },
						}}
					>
						{t("sentiment.negative.all")}
					</Drill>
				</>
			)}
		</Section>
	);
}
